import { FC, ReactNode, useState } from 'react';
import {
  Select,
  MenuItem,
  SelectProps,
  Box,
  CircularProgress,
} from '@mui/material';
import { TranslatableString } from 'i18n/translations/no';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ReactComponent as TickIcon } from 'atoms/icons/svg/tick.svg';
import './Dropdown.scss';

type Option = {
  label: string;
  value: string | number;
  icon?: ReactNode;
};

export type DropDownProps = {
  isLoading?: boolean;
  label?: TranslatableString;
  icon?: ReactNode;
  options: Option[];
  showEmptyOption?: boolean;
  clickableNodeOverride?: ReactNode;
} & SelectProps;

const Dropdown: FC<DropDownProps> = ({
  icon,
  label,
  options,
  isLoading,
  value = '',
  showEmptyOption = false,
  clickableNodeOverride,
  ...rest
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Box className="remora-dropdown-wrapper">
      <Select
        className={`dropdown ${open ? 'active' : ''}`}
        variant="standard"
        size="small"
        displayEmpty
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        IconComponent={KeyboardArrowDown}
        renderValue={(selected) => {
          const option = options.find((opt) => opt.value === selected);

          if (clickableNodeOverride) {
            return clickableNodeOverride;
          }

          return (
            <span className="label-section">
              {icon && <span className="icon">{icon}</span>}
              {label && <span className="label">{label}</span>}

              {isLoading ? (
                <CircularProgress size={16} />
              ) : (
                <span className="selected">{option?.label ?? ''}</span>
              )}
            </span>
          );
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          className: 'remora-dropdown-menu',
        }}
        value={value}
        {...rest}
      >
        {showEmptyOption && (
          <MenuItem value="">{t('Select an option')}</MenuItem>
        )}

        {options.map((opt) => {
          const isSelected = opt.value === value;
          return (
            <MenuItem key={opt.value} className="option" value={opt.value}>
              {opt?.icon && <span className="icon">{opt?.icon}</span>}

              <span className={`label-wrapper ${isSelected ? 'active' : ''}`}>
                <span className="label">{opt.label}</span>
                {isSelected && <TickIcon />}
              </span>
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
};

export default Dropdown;
